import { useState, useContext } from 'react';
import { ReactComponent as CrossIcon } from 'assets/icon/x-circle.svg';
import CloudUpload from 'components/common/cloud-upload';
import PrimaryDarkButton from 'components/common/buttons/primary-dark-btn';
import DarkButton from 'components/common/buttons/dark-btn';
import { useAppDispatch, useAppSelector } from 'hooks/useAppDispatchSelector';
import { initModal, onlyNumbers, formatLocalizedDate } from 'helpers/utils';
import { setModal } from 'store/features/modelSlice';
import { ReactComponent as CancelIcon } from 'assets/icon/cancel.svg';
import { ReactComponent as PlayIcon } from 'assets/icon/play.svg';
import { setPreviewFileData } from 'store/features/modelSlice';
import { ReactComponent as SearchIcon } from 'assets/icon/search.svg';
import Textarea from 'components/common/textarea-field';
import { IUploadMediaResponse } from 'components/sidemodal/add-edit-workshop-appointment';
import { IUploadImageRequestParam } from 'store/features/organizationSlice';
import InputField from 'components/common/input-field';
import moment from 'moment';
import { createPayment, uploadPaymentMedia } from 'store/features/paymentSlice';
import { AppContext } from 'context/app-context';
import { ICreatePaymentRequestData } from 'interfaces/payment-interface';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
// import { useNavigate } from 'react-router-dom';

const Step3 = () => {
  const { setLoader } = useContext(AppContext);
  const dispatch = useAppDispatch();
  // const navigate = useNavigate();
  const { workshopAppointmentDetails } = useAppSelector(state => state.workShopAppointment);
  const { modal } = useAppSelector(state => state.modal);
  const { callBackFun } = modal.content;
  const { t } = useTranslation();

  const [files, setFiles] = useState<File[]>([]);
  const [paymentData, setPaymentData] = useState<{ amount: string; description: string }>({
    amount: '',
    description: '',
  });
  const [errors, setErrors] = useState<{ amount: string } | null>(null);
  const uploadMediaForAdd = async () => {
    try {
      const uploadMediaParam =
        files.map(data => {
          return {
            request: {
              file_name: data?.name || '',
              file_type: data?.type || '',
              file_size: data?.size || 0,
            },
            file: data,
          };
        }) || [];
      const mediaResponse: IUploadMediaResponse[] = await Promise.all(
        [...uploadMediaParam].map(data => {
          const params: IUploadImageRequestParam = {
            request: {
              file_name: data?.request?.file_name || '',
              file_type: data?.request?.file_type || '',
              file_size: data?.request?.file_size || 0,
            },
            file: data?.file,
          };
          const res = dispatch(uploadPaymentMedia({ params }));
          return res;
        }),
      );
      return Promise.resolve(mediaResponse);
    } catch (error) {
      return Promise.reject(error);
    }
  };
  const isValid = () => {
    if (!paymentData?.amount?.trim()) {
      setErrors({ ...errors, amount: t('workshopAppointment.steps.errorAmountMessage') });
      return false;
    } else {
      setErrors(null);
      return true;
    }
  };
  const handleSubmit = async () => {
    try {
      if (!isValid()) {
        return;
      }
      setLoader(true);

      const uploadMedia = files.length
        ? await uploadMediaForAdd().catch(err => {
            return;
          })
        : [];

      let request: ICreatePaymentRequestData = {
        amount: paymentData?.amount,
      };
      if (uploadMedia?.length) {
        request = {
          ...request,
          media: uploadMedia?.length ? uploadMedia.map(data => data?.key || '') : [],
        };
      }

      request = {
        ...request,
        workshop_id: workshopAppointmentDetails?._id,
        organization_id: workshopAppointmentDetails?.vehicle_data?.organization_id || '',
        user_id:
          workshopAppointmentDetails?.user_id ||
          workshopAppointmentDetails?.user_details?._id ||
          '',
      };
      if (paymentData?.description) {
        request = { ...request, description: paymentData?.description || '' };
      }
      await dispatch(
        createPayment({
          request: { ...request },
        }),
      ).catch(() => {
        setLoader(false);
      });
      toast.success(t('workshopAppointment.steps.successPaymentToastMessage'));
      dispatch(setModal(initModal));
      setLoader(false);
      // navigate('/billing');
    } catch (error) {
      setLoader(false);
    }
  };
  const handleSetFiles = (file: File) => {
    const updatedFiles = [...files, file];
    setFiles(updatedFiles);
  };
  const handlePreviewFile = (file: File, type: string) => {
    dispatch(
      setPreviewFileData({
        type: type || '',
        content: { url: window.URL.createObjectURL(file) },
      }),
    );
  };
  const getFileContent = (file: File) => {
    const splitedVal = file.type.split('/');
    switch (splitedVal[0]) {
      case 'image':
        return (
          <img
            src={window.URL.createObjectURL(file)}
            className="flex items-center justify-center min-w-full h-full w-12 object-contain"
            alt=""
            onClick={() => handlePreviewFile(file, splitedVal[0])}
          />
        );
      case 'video':
        return (
          <div
            className="flex items-center justify-center bg-grey3 w-12 h-full min-w-full rounded-3 cursor-pointer"
            onClick={() => handlePreviewFile(file, splitedVal[0])}
          >
            <PlayIcon />
          </div>
        );
      default:
        return (
          <div
            className="flex items-center justify-center bg-grey1 w-12 min-w-full h-full rounded-3 cursor-pointer"
            onClick={() => handlePreviewFile(file, splitedVal[0])}
          >
            <SearchIcon />
          </div>
        );
    }
  };
  const handleRemoveFiles = (index: number) => {
    const updatedFiles = (files || []).filter((file, i) => i !== index);
    setFiles(updatedFiles);
  };
  return (
    <>
      <div className="flex items-center w-full">
        <div className="flex items-center flex-1">
          <label className="pl-2 text-xl font-semibold text-black2">
            {t('workshopAppointment.steps.createPaymentLabel')}
          </label>
        </div>
        <div
          className="flex items-center justify-center cursor-pointer"
          onClick={() => {
            dispatch(setModal(initModal));
          }}
        >
          <CrossIcon height={32} width={32} />
        </div>
      </div>
      <div className="flex flex-wrap w-full overflow-y-auto max-h-[calc(100vh-200px)]  gap-y-4">
        <div className="flex w-full">
          <label className="text-sm text-black2 break-words font-medium">
            {t('workshopAppointment.steps.uploadInvoiceImageLabel')}
          </label>
        </div>
        <div className="flex w-full">
          <CloudUpload
            setFile={e => {
              if (e?.target?.files?.length) {
                handleSetFiles(e?.target?.files[0]);
              } else {
                e.stopPropagation();
              }
            }}
            title={t('workshopAppointment.steps.uploadFileTitle')}
          />
        </div>
        {files && files?.length ? (
          <div className="w-full flex items-center justify-start overflow-hidden">
            <div className="overflow-x-auto w-full max-w-full flex py-2">
              {[...files].map((file, index) => (
                <div
                  className="flex items-center justify-center ml-2 rounded-3 h-12 w-12 max-w-12 min-h-full min-w-fit bg-grey1 relative"
                  key={`${file?.name}-${index}`}
                >
                  <CancelIcon
                    className="flex absolute -top-1 -right-1 cursor-pointer"
                    onClick={() => handleRemoveFiles(index)}
                  />
                  {getFileContent(file)}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <></>
        )}
        <div className="flex flex-wrap w-full overflow-y-auto max-h-[calc(100vh-250px)] gap-y-1 bg-grey4 rounded-4 p-4">
          <div className="flex items-center w-full gap-x-2">
            <label className="font-medium text-sm">
              {t('workshopAppointment.steps.customerName')}:
            </label>
            <label className="text-sm">{`${workshopAppointmentDetails?.user_details?.name} ${workshopAppointmentDetails?.user_details?.surname}`}</label>
          </div>
          <div className="flex items-center w-full gap-x-2">
            <label className="font-medium text-sm">
              {t('workshopAppointment.ExpandedUi.customer_email')}:
            </label>
            <label className="text-sm">{workshopAppointmentDetails?.user_details?.email}</label>
          </div>
          <div className="flex items-center w-1/2 gap-x-2">
            <label className="font-medium text-sm">{t('workshopAppointment.steps.time')}:</label>
            <label className="text-sm">
              {formatLocalizedDate(
                workshopAppointmentDetails?.slot
                  ? `${workshopAppointmentDetails.slot.date} ${workshopAppointmentDetails.slot.start_time}`
                  : null,
                t,
                true,
                'YYYY-MM-DD HH:mm',
              )}
            </label>
          </div>
          <div className="flex items-center w-1/2 gap-x-2">
            <label className="font-medium text-sm">Plate:</label>
            <label className="text-sm">{workshopAppointmentDetails?.vehicle_data?.plate}</label>
          </div>
          <div className="flex items-center w-full gap-x-2">
            <label className="font-medium text-sm">
              {t('workshopAppointment.tableHeaders.service_type')}:
            </label>
            <label className="text-sm">
              {workshopAppointmentDetails?.other_service_type
                ? workshopAppointmentDetails?.other_service_type
                : workshopAppointmentDetails?.alert_details
                ? workshopAppointmentDetails?.alert_details?.title
                : '-'}
            </label>
          </div>
          <div className="flex items-center w-1/2 gap-x-2">
            <label className="font-medium text-sm">{t('BRAND')}:</label>
            <label className="text-sm">{workshopAppointmentDetails?.brand_data?.name}</label>
          </div>
          <div className="flex items-center w-1/2 gap-x-2">
            <label className="font-medium text-sm">{t('MODEL')}:</label>
            <label className="text-sm">{workshopAppointmentDetails?.model_data?.name}</label>
          </div>
        </div>
        <div className="w-full">
          <label className="font-medium text-sm text-black2 w-full">
            {t('workshopAppointment.steps.amount')}
          </label>
          <InputField
            placeholder={t('workshopAppointment.steps.amountPlaceholder')}
            value={paymentData?.amount}
            onChange={val => {
              if (val && !onlyNumbers.test(val)) {
                return;
              }
              setPaymentData({ ...paymentData, amount: val });
            }}
          />
        </div>
        <div className="w-full">
          <label className="font-medium text-sm text-black2 w-full">
            {t('workshopAppointment.steps.descriptionLabel')}
          </label>
          <Textarea
            placeholder={t('workshopAppointment.steps.descriptionPlaceholder')}
            value={paymentData?.description}
            onChange={val => setPaymentData({ ...paymentData, description: val })}
            row={2}
          />
        </div>
      </div>
      <div className="flex items-center justify-center w-full gap-x-4 mt-4">
        <PrimaryDarkButton label={'Create Payment'} onClick={() => handleSubmit()} width="w-1/2" />
        <DarkButton
          label={t('CANCEL')}
          width="w-1/2"
          onClick={() => {
            callBackFun && callBackFun();
            dispatch(setModal(initModal));
          }}
        />
      </div>
    </>
  );
};

export default Step3;
